import type {CanvasBackend, Context2D, DecodedSource} from './canvas-backend';

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Image element failed to load.'));
    image.src = url;
  });

/**
 * Main-thread fallback for engines without `OffscreenCanvas`: `<img>` decodes
 * with EXIF orientation applied (the default `image-orientation`), `<canvas>`
 * draws and encodes.
 */
export const canvasElementBackend: CanvasBackend<HTMLCanvasElement> = {
  createCanvas: (width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  },
  getContext: (canvas): Context2D | null => canvas.getContext('2d'),
  decode: async (blob): Promise<DecodedSource> => {
    const url = URL.createObjectURL(blob);
    try {
      const image = await loadImage(url);
      return {
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        release: () => URL.revokeObjectURL(url),
      };
    } catch (cause) {
      URL.revokeObjectURL(url);
      throw cause;
    }
  },
  toBlob: (canvas, type, quality) =>
    new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => (blob === null ? reject(new Error('Canvas produced no blob.')) : resolve(blob)),
        type,
        quality,
      );
    }),
};
